<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{
      $t("bikeVideo.pageTitle")
    }}</app-top-bar>
    <v-main
      :style="mainStyle"
      :class="hasBackgroundImage ? '' : 'bg-background'"
      class="text-on-surface-variant"
    >
      <v-container v-if="!showComplete && !util.isMobile">
        <media-desktop-handoff :description="$t('bikeVideo.handoffDescription')" />
      </v-container>

      <v-container v-if="!showComplete && util.isMobile">
        <h1 class="page-title mb-6">
          {{ title }}
        </h1>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="mb-0" v-html="subtitle" />

        <v-sheet v-if="!isVideoAvailable" class="mt-6">
          <div class="d-flex flex-column ga-3">
            <div
              v-for="(rule, index) in rules"
              :key="'rule' + index"
              class="d-flex flex-row align-center"
            >
              <v-img class="mr-3" height="24" max-width="24px" :src="rule.img" width="24" />
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p class="mb-0" v-html="rule.text" />
            </div>
          </div>

          <p class="mt-9 mb-2 font-weight-bold">{{ $t("bikeVideo.playStepsTitle") }}</p>
          <video
            autoplay
            class="w-100 h-100 rounded-lg"
            loop
            muted
            src="https://cdn.bikehero.com.br/assets/brands/caloi/caloi-video-exemplo-2.mp4"
          />
        </v-sheet>

        <video
          v-if="isVideoAvailable"
          autoplay
          class="img-preview rounded-lg mt-6"
          loop
          muted
          :src="videoUrl"
          style="max-width: 500px"
        />

        <image-uploader
          ref="videoInput"
          accept="video/*"
          :capture="'environment'"
          :max-height="1920"
          :max-width="1920"
          output-format="file"
          :preview="false"
          :quality="0.9"
          @input="onVideoInput"
        />

        <v-progress-linear
          v-show="isAdvancing && video"
          v-model="progressBar"
          class="mt-5"
          color="primary"
          height="25"
          :query="true"
          striped
        >
          <strong>{{ Math.ceil(progressBar) }}%</strong>
        </v-progress-linear>
        <p v-if="isAdvancing && video" class="mt-2 text-center text-caption">
          {{ isCompressing ? $t("bikeVideo.compressingLabel") : $t("bikeVideo.uploadingLabel") }}
        </p>

        <v-overlay :model-value="isProcessingMedia" class="align-center justify-center">
          <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer v-if="util.isMobile">
      <template v-if="!isVideoAvailable">
        <continue-footer-button color="primary" @click="onPromptVideo">
          {{ $t("bikeVideo.continueButton") }}
        </continue-footer-button>
      </template>
      <template v-else>
        <footer-button color="primary" :loading="isAdvancing" @click="advance">
          {{ $t("bikeVideo.looksGoodButton") }}
        </footer-button>
        <footer-button
          variant="text"
          color="primary"
          :disabled="isAdvancing"
          @click="onPromptVideo"
        >
          {{ $t("bikeVideo.recordAnotherButton") }}
        </footer-button>
      </template>
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, useTemplateRef } from "vue";
  import { useI18n } from "vue-i18n";
  import ImageUploader from "vue3-image-upload-resize";

  import qr from "@/assets/images/icons/qr-32.svg";
  import sun from "@/assets/images/icons/sun-32.svg";
  import timer from "@/assets/images/icons/timer-32.svg";
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useUtils } from "@/composables/useUtils";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";

  const { t } = useI18n();

  const util = useUtils();
  const app = useAppStore();
  const api = useApiStore();
  const { mainStyle, hasBackgroundImage } = usePageBackground();
  const pathPage = "/video";

  const COMPRESSION_THRESHOLD = 50 * 1024 * 1024;
  const compressionEnabled = import.meta.env.VITE_APP_VIDEO_COMPRESSION_ENABLED !== "false";
  const FFMPEG_CORE_BASE = "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd";

  const isComplete = computed(() => api.isItemComplete("video"));
  const {
    isAdvancing,
    showComplete,
    advance: advanceFlow,
  } = useStepFlow(pathPage, () => isComplete.value);
  const isProcessingMedia = ref(false);
  const isCompressing = ref(false);
  const video = ref<File | null>(null);
  const videoUrl = computed(() => URL.createObjectURL(video.value!));
  const progressBar = ref(0);
  let interval: ReturnType<typeof setInterval> | null = null;

  const videoInputRef = useTemplateRef("videoInput");
  const isVideoAvailable = computed(() => !isComplete.value && video.value !== null);

  const title = computed(() =>
    isVideoAvailable.value ? t("bikeVideo.titleReview") : t("bikeVideo.titleTake"),
  );
  const subtitle = computed(() =>
    isVideoAvailable.value ? t("bikeVideo.subtitleReview") : t("bikeVideo.subtitleTake"),
  );

  async function onVideoInput(file: unknown) {
    isProcessingMedia.value = true;
    await new Promise((resolve) => setTimeout(resolve, 150)); // Garante o render do overlay na tela
    try {
      if (!(file instanceof File)) return;
      video.value = file;
    } finally {
      isProcessingMedia.value = false;
    }
  }

  async function onPromptVideo() {
    if (isComplete.value) {
      await advanceFlow();
      return;
    }
    const input = videoInputRef.value.$el.querySelector("input[type=file]");
    input.click();
  }

  async function compressVideo(file: File): Promise<File> {
    const { FFmpeg } = await import("@ffmpeg/ffmpeg");
    const { fetchFile, toBlobURL } = await import("@ffmpeg/util");

    const ffmpeg = new FFmpeg();

    ffmpeg.on("progress", ({ progress }: { progress: number }) => {
      progressBar.value = Math.round(progress * 55);
    });

    await ffmpeg.load({
      coreURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, "text/javascript"),
      wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, "application/wasm"),
    });

    await ffmpeg.writeFile("input", await fetchFile(file));

    await ffmpeg.exec([
      "-i",
      "input",
      "-c:v",
      "libx264",
      "-crf",
      "30",
      "-preset",
      "ultrafast",
      "-vf",
      "scale=-2:720",
      "-c:a",
      "aac",
      "-b:a",
      "96k",
      "output.mp4",
    ]);

    const data = (await ffmpeg.readFile("output.mp4")) as Uint8Array;
    return new File([data], "video.mp4", { type: "video/mp4" });
  }

  async function advance() {
    try {
      await advanceFlow(async () => {
        progressBar.value = 0;

        let fileToUpload: File = video.value!;

        if (compressionEnabled && video.value!.size > COMPRESSION_THRESHOLD) {
          isCompressing.value = true;
          try {
            fileToUpload = await compressVideo(video.value!);
          } catch (e) {
            console.error("Failed to compress video", e);
            fileToUpload = video.value!;
          } finally {
            isCompressing.value = false;
          }
        }

        progressBar.value = 60;
        interval = setInterval(() => {
          if (progressBar.value >= 90) {
            if (interval) clearInterval(interval);
            return;
          }
          progressBar.value += 5;
        }, 1000);

        await api.sendBikeMedia({ type: "BIKE_VIDEO", file: fileToUpload });
        progressBar.value = 100;
      });
    } catch (error) {
      console.error(error);
    }
  }

  const rules = [
    {
      img: qr,
      text: t("bikeVideo.rule1Text"),
    },
    {
      img: sun,
      text: t("bikeVideo.rule2Text"),
    },
    {
      img: timer,
      text: t("bikeVideo.rule3Text"),
    },
  ];

  onBeforeUnmount(() => {
    if (interval) clearInterval(interval);
  });
</script>

<style scoped>
  :deep(.img-preview) {
    height: 100% !important;
    width: 100% !important;
  }

  :deep(.fileinput) {
    display: none;
  }

  p {
    margin-bottom: 1.25rem;
  }
</style>
